import React from 'react';

import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Button,
  Flex,
  useColorModeValue,
  Collapse,
  Divider,
  Badge,
  UnorderedList,
  ListItem,
  Input,
  InputGroup,
  InputLeftElement,
  Grid,
  GridItem,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid
} from '@chakra-ui/react';
import { useCustomToast } from '../utils/customToast';
import {
  FaBalanceScale,
  FaSearch,
  FaInfoCircle,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { getReviews } from '../services/reviewService';
import { getYearData } from '../services/yearService';
import ReactMarkdown from 'react-markdown';
import { logger } from '../utils/logger';
import { SectionLabel } from './dashboards/DashboardWrapper';
// threats
import { GrThreats } from 'react-icons/gr';
// opportunities
import { GiTrophy } from 'react-icons/gi';
// strengths
import { GiStrongMan } from 'react-icons/gi';
// weaknesses
import { MdOutlineSick } from 'react-icons/md';

const markdownComponents = {
  p: (props) => (
    <Text
      mb={4}
      color='gray.700'
      {...props}
    />
  ),
  strong: (props) => (
    <Text
      as='span'
      fontWeight='bold'
      color='indigo.700'
      {...props}
    />
  ),
  em: (props) => (
    <Text
      as='span'
      fontStyle='italic'
      {...props}
    />
  ),
  h1: (props) => (
    <Heading
      size='xl'
      mt={4}
      mb={2}
      color='indigo.700'
      {...props}
    />
  ),
  h2: (props) => (
    <Heading
      size='lg'
      mt={4}
      mb={2}
      color='indigo.600'
      {...props}
    />
  ),
  h3: (props) => (
    <Heading
      size='md'
      mt={4}
      mb={2}
      color='indigo.500'
      {...props}
    />
  ),
  ul: ({ children, ...props }) => {
    return (
      <UnorderedList
        pl={4}
        mb={4}
        spacing={1}
        {...props}
      >
        {children}
      </UnorderedList>
    );
  },
  li: ({ children, ...props }) => {
    return (
      <ListItem
        mb={1}
        color='gray.700'
        {...props}
      >
        {children}
      </ListItem>
    );
  }
};

const SwotAnalysis = ({ content }) => {
  // Define colors for each quadrant
  const quadrantColors = {
    strengths: { bg: 'green.50', border: 'green.200', heading: 'green.600' },
    weaknesses: { bg: 'red.50', border: 'red.200', heading: 'red.600' },
    opportunities: { bg: 'blue.50', border: 'blue.200', heading: 'blue.600' },
    threats: { bg: 'orange.50', border: 'orange.200', heading: 'orange.600' }
  };

  // Parse the content to extract SWOT sections
  const sections = {
    strengths: '',
    weaknesses: '',
    opportunities: '',
    threats: ''
  };

  // Improved regex patterns that can handle markdown formatting with ** asterisks
  const strengthsRegex = /\*\*\s*strengths:?\s*\*\*/i;
  const weaknessesRegex = /\*\*\s*weaknesses:?\s*\*\*/i;
  const opportunitiesRegex = /\*\*\s*opportunities:?\s*\*\*/i;
  const threatsRegex = /\*\*\s*threats:?\s*\*\*/i;

  // Try to parse the content
  let hasFoundSections = false;

  // Try to find section positions
  const strengthsMatch = content.match(strengthsRegex);
  const weaknessesMatch = content.match(weaknessesRegex);
  const opportunitiesMatch = content.match(opportunitiesRegex);
  const threatsMatch = content.match(threatsRegex);

  // Only proceed if we find at least strengths and weaknesses
  if (strengthsMatch && weaknessesMatch) {
    hasFoundSections = true;
    const strengthsPos = strengthsMatch.index;
    const weaknessesPos = weaknessesMatch.index;
    const opportunitiesPos = opportunitiesMatch ? opportunitiesMatch.index : -1;
    const threatsPos = threatsMatch ? threatsMatch.index : -1;

    // Extract content between sections
    if (opportunitiesPos > weaknessesPos) {
      sections.strengths = content
        .substring(strengthsPos + strengthsMatch[0].length, weaknessesPos)
        .trim();

      sections.weaknesses = content
        .substring(weaknessesPos + weaknessesMatch[0].length, opportunitiesPos)
        .trim();

      if (threatsPos > opportunitiesPos) {
        sections.opportunities = content
          .substring(
            opportunitiesPos + opportunitiesMatch[0].length,
            threatsPos
          )
          .trim();

        sections.threats = content
          .substring(threatsPos + threatsMatch[0].length)
          .trim();
      } else {
        sections.opportunities = content
          .substring(opportunitiesPos + opportunitiesMatch[0].length)
          .trim();
      }
    } else {
      sections.strengths = content
        .substring(strengthsPos + strengthsMatch[0].length, weaknessesPos)
        .trim();

      sections.weaknesses = content
        .substring(weaknessesPos + weaknessesMatch[0].length)
        .trim();
    }
  }

  // If we couldn't parse with the markdown format, try plain text format
  if (!hasFoundSections) {
    // Plain text regex patterns
    const plainStrengthsRegex = /strengths:?(?:\s*\n|\s*$)/i;
    const plainWeaknessesRegex = /weaknesses:?(?:\s*\n|\s*$)/i;
    const plainOpportunitiesRegex = /opportunities:?(?:\s*\n|\s*$)/i;
    const plainThreatsRegex = /threats:?(?:\s*\n|\s*$)/i;

    // Try to find section positions
    const strengthsMatch = content.match(plainStrengthsRegex);
    const weaknessesMatch = content.match(plainWeaknessesRegex);
    const opportunitiesMatch = content.match(plainOpportunitiesRegex);
    const threatsMatch = content.match(plainThreatsRegex);

    if (strengthsMatch && weaknessesMatch) {
      hasFoundSections = true;
      const strengthsPos = strengthsMatch.index;
      const weaknessesPos = weaknessesMatch.index;
      const opportunitiesPos = opportunitiesMatch
        ? opportunitiesMatch.index
        : -1;
      const threatsPos = threatsMatch ? threatsMatch.index : -1;

      // Extract content between sections
      sections.strengths = content
        .substring(strengthsPos + strengthsMatch[0].length, weaknessesPos)
        .trim();

      if (opportunitiesPos > weaknessesPos) {
        sections.weaknesses = content
          .substring(
            weaknessesPos + weaknessesMatch[0].length,
            opportunitiesPos
          )
          .trim();

        if (threatsPos > opportunitiesPos) {
          sections.opportunities = content
            .substring(
              opportunitiesPos + opportunitiesMatch[0].length,
              threatsPos
            )
            .trim();

          sections.threats = content
            .substring(threatsPos + threatsMatch[0].length)
            .trim();
        } else {
          sections.opportunities = content
            .substring(opportunitiesPos + opportunitiesMatch[0].length)
            .trim();
        }
      } else {
        sections.weaknesses = content
          .substring(weaknessesPos + weaknessesMatch[0].length)
          .trim();
      }
    }
  }

  // If still no sections found, try one more alternative parsing method
  if (!hasFoundSections) {
    // Look for markdown sections by using line-based approach
    const lines = content.split('\n');
    let currentSection = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const lowerLine = line.toLowerCase();

      if (
        line.match(/\*\*\s*strengths:?\s*\*\*/i) ||
        lowerLine === '**strengths**' ||
        lowerLine === 'strengths:' ||
        lowerLine === '## strengths'
      ) {
        currentSection = 'strengths';
        hasFoundSections = true;
        continue;
      } else if (
        line.match(/\*\*\s*weaknesses:?\s*\*\*/i) ||
        lowerLine === '**weaknesses**' ||
        lowerLine === 'weaknesses:' ||
        lowerLine === '## weaknesses'
      ) {
        currentSection = 'weaknesses';
        hasFoundSections = true;
        continue;
      } else if (
        line.match(/\*\*\s*opportunities:?\s*\*\*/i) ||
        lowerLine === '**opportunities**' ||
        lowerLine === 'opportunities:' ||
        lowerLine === '## opportunities'
      ) {
        currentSection = 'opportunities';
        continue;
      } else if (
        line.match(/\*\*\s*threats:?\s*\*\*/i) ||
        lowerLine === '**threats**' ||
        lowerLine === 'threats:' ||
        lowerLine === '## threats'
      ) {
        currentSection = 'threats';
        continue;
      }

      if (currentSection && line) {
        sections[currentSection] += line + '\n';
      }
    }
  }

  // If we still couldn't parse the sections, just show the original content
  if (!hasFoundSections) {
    console.log('Could not parse SWOT sections from content:', content);
    return (
      <Box>
        <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
      </Box>
    );
  }

  // Ensure we have placeholders for empty sections
  if (!sections.opportunities || sections.opportunities.trim() === '') {
    sections.opportunities = 'No specific opportunities identified.';
  }

  if (!sections.threats || sections.threats.trim() === '') {
    sections.threats = 'No specific threats identified.';
  }

  return (
    <Grid
      templateColumns='repeat(2, 1fr)'
      templateRows='auto auto'
      gap={3}
      width='100%'
      borderRadius='md'
      overflow='hidden'
    >
      {/* Strengths - Top Left */}
      <Box
        bg={quadrantColors.strengths.bg}
        p={4}
        borderWidth='1px'
        borderColor={quadrantColors.strengths.border}
        borderRadius='md'
      >
        <Heading
          size='md'
          mb={2}
          color={quadrantColors.strengths.heading}
        >
          <Flex
            width='100%'
            justifyContent='space-between'
            alignItems='center'
          >
            Strengths
            <Icon
              as={GiStrongMan}
              boxSize={10}
            />
          </Flex>
        </Heading>
        <Box>
          <ReactMarkdown components={markdownComponents}>
            {sections.strengths}
          </ReactMarkdown>
        </Box>
      </Box>

      {/* Weaknesses - Top Right */}
      <Box
        bg={quadrantColors.weaknesses.bg}
        p={4}
        borderWidth='1px'
        borderColor={quadrantColors.weaknesses.border}
        borderRadius='md'
      >
        <Heading
          size='md'
          mb={2}
          color={quadrantColors.weaknesses.heading}
        >
          <Flex
            width='100%'
            justifyContent='space-between'
            alignItems='center'
          >
            Weaknesses
            <Icon
              as={MdOutlineSick}
              boxSize={10}
            />
          </Flex>
        </Heading>
        <Box>
          <ReactMarkdown components={markdownComponents}>
            {sections.weaknesses}
          </ReactMarkdown>
        </Box>
      </Box>

      {/* Opportunities - Bottom Left */}
      <Box
        bg={quadrantColors.opportunities.bg}
        p={4}
        borderWidth='1px'
        borderColor={quadrantColors.opportunities.border}
        borderRadius='md'
      >
        <Heading
          size='md'
          mb={2}
          color={quadrantColors.opportunities.heading}
        >
          <Flex
            width='100%'
            justifyContent='space-between'
            alignItems='center'
          >
            Opportunities
            <Icon
              as={GiTrophy}
              boxSize={10}
            />
          </Flex>
        </Heading>
        <Box>
          <ReactMarkdown components={markdownComponents}>
            {sections.opportunities}
          </ReactMarkdown>
        </Box>
      </Box>

      {/* Threats - Bottom Right */}
      <Box
        bg={quadrantColors.threats.bg}
        p={4}
        borderWidth='1px'
        borderColor={quadrantColors.threats.border}
        borderRadius='md'
      >
        <Heading
          size='md'
          mb={2}
          color={quadrantColors.threats.heading}
        >
          <Flex
            width='100%'
            justifyContent='space-between'
            alignItems='center'
          >
            Threats
            <Icon
              as={GrThreats}
              boxSize={10}
            />
          </Flex>
        </Heading>
        <Box>
          <ReactMarkdown components={markdownComponents}>
            {sections.threats}
          </ReactMarkdown>
        </Box>
      </Box>
    </Grid>
  );
};


export default SwotAnalysis;